import React, { useMemo } from "react";
import { Divider, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { formatMoney } from "../../utils/hotelBookingUtils";
import { BookingRoomRow, HotelBookingTexts } from "./HotelBookingTypes";

type Props = {
    rooms: BookingRoomRow[];
    t: HotelBookingTexts;
};

export default function BookingSummary({ rooms, t }: Props) {
    const totalPrice = useMemo(() => rooms.reduce((sum, row) => sum + row.totalPrice, 0), [rooms]);

    const summaryRows = useMemo(() => {
        const map = new Map<string, { count: number; nights: number; total: number }>();

        rooms.forEach((room) => {
            const existing = map.get(room.roomTypeName) ?? { count: 0, nights: 0, total: 0 };
            existing.count += 1;
            existing.nights += room.nights;
            existing.total += room.totalPrice;
            map.set(room.roomTypeName, existing);
        });

        return Array.from(map.entries()).map(([name, value]) => ({ name, ...value }));
    }, [rooms]);

    return (
        <Paper withBorder p="lg" radius="md">
            <Stack gap="md">
                <Title order={4}>{t.summary}</Title>

                {summaryRows.length === 0 ? (
                    <Text c="dimmed">-</Text>
                ) : (
                    summaryRows.map((row) => (
                        <Group key={row.name} justify="space-between">
                            <Text>{row.name}</Text>
                            <Text c="dimmed">
                                {row.count} rooms · {row.nights} nights
                            </Text>
                            <Text fw={700}>{formatMoney(row.total)}</Text>
                        </Group>
                    ))
                )}

                <Divider />

                <Group justify="space-between">
                    <Text fw={800}>{t.totalPrice}</Text>
                    <Text size="xl" fw={900} c="green">
                        {formatMoney(totalPrice)}
                    </Text>
                </Group>
            </Stack>
        </Paper>
    );
}
